import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { checkAndRefreshTokenIfNeeded } from '../utils/checkAndRefreshTokenIfNeeded';
import { clearAuthSession } from '../utils/auth'; 
import { getAppUrlInfo } from '../utils/urls';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

interface ApiClientOptions {
    method: HttpMethod;
    url: string;
    data?: any;
    headers?: Record<string, string>;
    isFormData?: boolean;
    queryParams?: Record<string, string | number>;
}

export const getDefaultHeaders = () => ({
    'Content-Type': 'application/json',
    Accept: 'application/json',
});

const removeHeaderCaseInsensitive = (headers: Record<string, string>, keyToRemove: string) => {
    Object.keys(headers).forEach((key) => {
        if (key.toLowerCase() === keyToRemove.toLowerCase()) {
            delete headers[key];
        }
    });
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const apiClient = async <T>({
    method,
    url,
    data,
    headers = {},
    isFormData = false,
    queryParams = {},
}: ApiClientOptions):Promise<AxiosResponse<T>> => {
    try {
        await checkAndRefreshTokenIfNeeded();

        const defaultHeaders: Record<string, string> = getDefaultHeaders();
        const requestHeaders: Record<string, string> = { ...headers };
        const isNativeFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        const shouldTreatAsFormData = isFormData || isNativeFormData;

        // Let browser/axios set multipart boundary automatically.
        if (shouldTreatAsFormData) {
            removeHeaderCaseInsensitive(defaultHeaders, 'Content-Type');
            removeHeaderCaseInsensitive(requestHeaders, 'Content-Type');
        }

        const mergedGetParams =
            method === 'get'
                ? { ...queryParams, ...(isPlainObject(data) ? data : {}) }
                : queryParams;

        const config: AxiosRequestConfig = {
            method,
            url,
            withCredentials: true,
            headers: {
                ...defaultHeaders,
                ...requestHeaders,
            },
            params: mergedGetParams,
            ...(method !== 'get' ? { data } : {}),
        };

        const response = await axios<T>(config); 
        return response;
    } catch (err: any) {
        //console.log('[API Error]', err);
        const {accountsUrl} =  getAppUrlInfo();

        if (err?.response?.status === 401) {
            clearAuthSession();
            window.location.href = `${accountsUrl}/login`;
        }

        // Throw structured error for UI to handle
        throw {...err?.response?.data};
    }
};
